import { ReactElement, useEffect } from 'react'

import { useAuthDispatch } from '@percent/cause-dashboard/common/hooks/useAuthDispatch/useAuthDispatch'
import { useAuthState } from '@percent/cause-dashboard/common/hooks/useAuthState/useAuthState'
import { SET_SESSION_EXPIRED } from '@percent/cause-dashboard/context/auth'
import { percentClient } from '@percent/cause-dashboard/capabilities/percentClient'
import { useCausesPortalAnalytics } from '@percent/cause-dashboard/common/hooks'
import { ReauthenticationModal } from './ReauthenticationModal/ReauthenticationModal'
import { useReauthentication } from './useReauthentication/useReauthentication'

const isLoginRequest = (url?: string) => url?.includes('auth/login')

export const WithAxios = ({ children }: { children: ReactElement }) => {
  const { authDispatch } = useAuthDispatch()
  const { authState } = useAuthState()
  const { reset: resetAnalytics } = useCausesPortalAnalytics()

  const { reauthModalOpen, requestReauthentication, handleReauthSuccess, handleReauthCancel } = useReauthentication()

  useEffect(() => {
    const expireSession = () => {
      authDispatch({ type: SET_SESSION_EXPIRED })
      resetAnalytics()
    }

    const id = percentClient.interceptors.response.use(
      responseConfig => responseConfig,
      async error => {
        const originalRequest = error?.config

        const isForbidden = error?.response?.status === 403
        const isReauthRequired = error?.response?.data?.error?.code === 'auth/reauthentication_required'
        const isFirstReauthAttempt = !originalRequest?._isReauthRetry
        const isNotLoginRequest = !isLoginRequest(originalRequest?.url)

        const shouldReauthenticate = isForbidden && isReauthRequired && isFirstReauthAttempt && isNotLoginRequest

        if (shouldReauthenticate) {
          try {
            await requestReauthentication()
            originalRequest._isReauthRetry = true

            return percentClient(originalRequest)
          } catch {
            throw error
          }
        }

        if (error?.response?.status === 401) {
          expireSession()
        }

        throw error
      }
    )

    return () => percentClient.interceptors.response.eject(id)
  }, [authDispatch, resetAnalytics, requestReauthentication])

  return (
    <>
      {children}
      {reauthModalOpen && (
        <ReauthenticationModal
          open={reauthModalOpen}
          email={authState.user?.email}
          onSuccess={handleReauthSuccess}
          onCancel={handleReauthCancel}
        />
      )}
    </>
  )
}
